<template>
  <base-modal
    ref="modalRef"
    :nested="false"
    size="lg"
  >
    <template #header>
      <h4>
        <span v-if="assetName">{{ $t('components.assets.images.title') }}: {{ assetName }}</span>
        <span v-else>{{ $t('components.assets.images.title') }}</span>
      </h4>
    </template>

    <template #body>
      <div class="asset-image-form">
        <div
          v-if="errorMessage"
          class="alert alert-danger d-flex align-items-center gap-2 mb-4"
        >
          <i class="bi bi-exclamation-triangle-fill" />
          <span>{{ errorMessage }}</span>
        </div>

        <div class="form-section">
          <h5 class="form-section-title">
            <i class="bi bi-pencil-square me-2" />
            {{ $t('components.assets.images.nameTitle') }}
          </h5>

          <div class="mb-3">
            <label class="form-label required">{{ $t('components.assets.images.nameLabel') }}</label>
            <input
              v-model="assetNameInput"
              type="text"
              class="form-control"
              :class="{ 'is-invalid': nameError }"
              :placeholder="$t('components.assets.images.namePlaceholder')"
            >
            <div
              v-if="nameError"
              class="invalid-feedback d-block"
            >
              {{ nameError }}
            </div>
          </div>

          <button
            type="button"
            class="btn btn-outline-primary btn-sm"
            :disabled="savingName"
            @click="saveAssetName"
          >
            <span
              v-if="savingName"
              class="spinner-border spinner-border-sm me-2"
            />
            {{ $t('components.assets.images.nameSave') }}
          </button>
        </div>

        <div class="form-section">
          <h5 class="form-section-title">
            <i class="bi bi-image me-2" />
            {{ $t('components.assets.images.uploadTitle') }}
          </h5>

          <div class="mb-3">
            <input
              id="assetImage"
              ref="imageInput"
              type="file"
              class="form-control"
              :class="{ 'is-invalid': imageError }"
              accept="image/jpeg,image/png,image/webp,image/gif"
              :aria-label="$t('components.assets.images.fields.image')"
              @change="handleImageChange"
            >
            <small class="text-muted">{{ $t('components.assets.images.hints.image') }}</small>
            <div
              v-if="imageError"
              class="invalid-feedback d-block"
            >
              {{ imageError }}
            </div>

            <div
              v-if="imagePreview"
              class="image-preview mt-2"
            >
              <img
                :src="imagePreview"
                alt="Asset preview"
              >
              <button
                type="button"
                class="btn-remove-preview"
                @click="clearImageSelection"
              >
                <i class="bi bi-x-circle-fill" />
              </button>
            </div>
          </div>

          <button
            type="button"
            class="btn btn-primary"
            :disabled="!imageFile || uploading"
            @click="uploadImage"
          >
            <span
              v-if="uploading"
              class="spinner-border spinner-border-sm me-2"
            />
            {{ $t('components.assets.images.uploadAction') }}
          </button>
        </div>

        <div class="form-section">
          <h5 class="form-section-title">
            <i class="bi bi-images me-2" />
            {{ $t('components.assets.images.listTitle') }}
          </h5>

          <div
            v-if="loadingImages"
            class="text-muted"
          >
            <span class="spinner-border spinner-border-sm me-2" />
            {{ $t('components.assets.images.loading') }}
          </div>
          <div
            v-else-if="images.length === 0"
            class="text-muted"
          >
            {{ $t('components.assets.images.empty') }}
          </div>
          <div
            v-else
            class="asset-image-grid"
          >
            <div
              v-for="image in images"
              :key="image.id"
              class="asset-image-card"
            >
              <div class="image-preview">
                <img
                  :src="convertPathToUrl(getImagePath(image))"
                  alt="Asset"
                >
              </div>
              <div class="asset-image-actions">
                <button
                  type="button"
                  class="btn btn-outline-danger btn-sm"
                  @click="requestDelete(image)"
                >
                  <i class="bi bi-trash me-1" />
                  {{ $t('common.remove') }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <template #footer>
      <div class="asset-image-footer">
        <div
          v-if="pendingDelete"
          class="delete-confirm"
        >
          <span>{{ $t('components.assets.images.deleteConfirm') }}</span>
          <button
            type="button"
            class="btn btn-outline-secondary btn-sm"
            :disabled="deleting"
            @click="cancelDelete"
          >
            {{ $t('common.no') }}
          </button>
          <button
            type="button"
            class="btn btn-danger btn-sm"
            :disabled="deleting"
            @click="confirmDelete"
          >
            <span
              v-if="deleting"
              class="spinner-border spinner-border-sm me-2"
            />
            {{ $t('common.remove') }}
          </button>
        </div>
        <button
          type="button"
          class="btn btn-secondary"
          @click="closeModal"
        >
          {{ $t('components.assets.images.close') }}
        </button>
      </div>
    </template>
  </base-modal>
</template>

<script lang="ts">
import { defineComponent, ref, getCurrentInstance } from 'vue'
import { useI18n } from 'vue-i18n'
import BaseModal from '@/components/content/_modals/core/BaseModal.vue'
import { addAssetImage, deleteAssetImage, getAssetImages, updateAssetName } from '@/crud/asset'

export default defineComponent({
  name: 'AssetImagesModal',
  components: { BaseModal },
  emits: ['saved'],
  setup(props, { emit }) {
    const { t } = useI18n()
    const vm = getCurrentInstance()
    const toast = vm?.proxy?.$toast

    const modalRef = ref<InstanceType<typeof BaseModal> | null>(null)
    const assetId = ref<number | string | null>(null)
    const assetTitle = ref<string>('')
    const assetNameInput = ref<string>('')
    const assetNameOriginal = ref<string>('')
    const assetSource = ref<'asset' | 'device'>('asset')

    const images = ref<any[]>([])
    const loadingImages = ref(false)
    const uploading = ref(false)
    const deleting = ref(false)
    const savingName = ref(false)
    const errorMessage = ref<string | null>(null)
    const pendingDelete = ref<any | null>(null)

    const imageInput = ref<HTMLInputElement | null>(null)
    const imageFile = ref<File | null>(null)
    const imagePreview = ref<string | null>(null)
    const imageError = ref<string | null>(null)
    const nameError = ref<string | null>(null)

    const validateImageFile = (file: File): string | null => {
      const maxSize = 5 * 1024 * 1024
      const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif']

      if (!validTypes.includes(file.type)) {
        return t('components.assets.images.validation.invalidType') as string
      }
      if (file.size > maxSize) {
        return t('components.assets.images.validation.tooLarge') as string
      }
      return null
    }

    const convertPathToUrl = (filePath: string): string => {
      let relativePath = filePath
      if (filePath.includes('/public/var/uploads/')) {
        relativePath = filePath.substring(filePath.indexOf('/var/uploads/'))
      } else if (filePath.includes('var/uploads/')) {
        const index = filePath.indexOf('var/uploads/')
        relativePath = '/' + filePath.substring(index)
      }

      if (!relativePath.startsWith('/')) {
        relativePath = '/' + relativePath
      }

      const base = (process.env.VUE_APP_API_BASE_URL || '').replace(/\/+$/, '')
      return base ? `${base}${relativePath}` : relativePath
    }

    const getImagePath = (image: any): string => {
      return image?.imagePath || image?.image_path || ''
    }

    const fetchImages = async () => {
      if (!assetId.value) {
        return
      }

      loadingImages.value = true
      errorMessage.value = null

      try {
        const response = await getAssetImages(
          assetId.value,
          assetSource.value === 'device' ? 'device' : undefined
        )
        images.value = response.data?.data || response.data || []
      } catch (e: any) {
        errorMessage.value = e?.response?.data?.error || e?.message || t('components.assets.images.error')
      } finally {
        loadingImages.value = false
      }
    }

    const handleImageChange = (event: Event) => {
      imageError.value = null
      const target = event.target as HTMLInputElement
      const file = target?.files?.[0] || null

      if (!file) {
        imageFile.value = null
        imagePreview.value = null
        return
      }

      const error = validateImageFile(file)
      if (error) {
        imageError.value = error
        imageFile.value = null
        imagePreview.value = null
        if (imageInput.value) {
          imageInput.value.value = ''
        }
        return
      }

      imageFile.value = file
      const reader = new FileReader()
      reader.onload = (e) => {
        imagePreview.value = e.target?.result as string
      }
      reader.readAsDataURL(file)
    }

    const clearImageSelection = () => {
      imageFile.value = null
      imagePreview.value = null
      imageError.value = null
      if (imageInput.value) {
        imageInput.value.value = ''
      }
    }

    const uploadImage = async () => {
      if (!assetId.value || !imageFile.value) {
        return
      }

      uploading.value = true
      errorMessage.value = null

      try {
        const formData = new FormData()
        formData.append('image', imageFile.value)

        await addAssetImage(
          assetId.value,
          formData,
          assetSource.value === 'device' ? 'device' : undefined
        )
        toast?.success(t('components.assets.images.uploadSuccess'))
        clearImageSelection()
        await fetchImages()
        emit('saved')
      } catch (e: any) {
        const errorMsg = e?.response?.data?.error || e?.message || t('components.assets.images.uploadError')
        errorMessage.value = errorMsg as string
        toast?.error(errorMsg)
      } finally {
        uploading.value = false
      }
    }

    const requestDelete = (image: any) => {
      if (!assetId.value || !image?.id) {
        return
      }
      pendingDelete.value = image
    }

    const cancelDelete = () => {
      pendingDelete.value = null
    }

    const confirmDelete = async () => {
      if (!assetId.value || !pendingDelete.value?.id) {
        return
      }

      deleting.value = true
      try {
        await deleteAssetImage(
          assetId.value,
          pendingDelete.value.id,
          assetSource.value === 'device' ? 'device' : undefined
        )
        toast?.success(t('components.assets.images.deleteSuccess'))
        pendingDelete.value = null
        await fetchImages()
        emit('saved')
      } catch (e: any) {
        const errorMsg = e?.response?.data?.error || e?.message || t('components.assets.images.deleteError')
        errorMessage.value = errorMsg as string
        toast?.error(errorMsg)
      } finally {
        deleting.value = false
      }
    }

    const closeModal = () => {
      modalRef.value?.closeModal()
    }

    const saveAssetName = async () => {
      if (!assetId.value) {
        return
      }

      const trimmedName = (assetNameInput.value || '').trim()
      if (trimmedName.length === 0) {
        nameError.value = t('components.assets.images.nameRequired') as string
        return
      }

      if (trimmedName === assetNameOriginal.value) {
        return
      }

      savingName.value = true
      nameError.value = null

      try {
        await updateAssetName(
          assetId.value,
          trimmedName,
          assetSource.value === 'device' ? 'device' : undefined
        )
        assetNameOriginal.value = trimmedName
        assetTitle.value = trimmedName
        toast?.success(t('components.assets.images.nameSaved'))
        emit('saved')
      } catch (e: any) {
        const errorMsg = e?.response?.data?.error || e?.message || t('components.assets.images.nameSaveError')
        nameError.value = errorMsg as string
        toast?.error(errorMsg)
      } finally {
        savingName.value = false
      }
    }

    const openModal = async (asset: any) => {
      assetId.value = asset?.id || null
      assetTitle.value = asset?.name || asset?.serial_number || ''
      assetNameInput.value = asset?.name || ''
      assetNameOriginal.value = asset?.name || ''
      assetSource.value = typeof asset?.id === 'string' ? 'device' : 'asset'
      errorMessage.value = null
      nameError.value = null
      clearImageSelection()
      images.value = []
      pendingDelete.value = null

      modalRef.value?.openModal()
      await fetchImages()
    }

    return {
      modalRef,
      assetName: assetTitle,
      assetNameInput,
      nameError,
      images,
      loadingImages,
      uploading,
      deleting,
      savingName,
      errorMessage,
      pendingDelete,
      imageInput,
      imageFile,
      imagePreview,
      imageError,
      openModal,
      closeModal,
      handleImageChange,
      clearImageSelection,
      uploadImage,
      requestDelete,
      cancelDelete,
      confirmDelete,
      saveAssetName,
      convertPathToUrl,
      getImagePath
    }
  }
})
</script>

<style scoped lang="scss">
@import '@/assets/scss/modal/asset-image-modal.scss';
</style>
